const CONFIG_KEY = 'football_predictor_config';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 часов в миллисекундах

class ConfigService {
  constructor() {
    this.config = this.loadConfig();
  }

  loadConfig() {
    try {
      const defaultConfig = { 
        leagues: null, 
        teams: {}, 
        fixtures: {}, 
        players: {}, 
        statistics: {},
        standings: {},
        predictions: {},
        countries: null,
        seasons: null,
        headToHead: {},
        transfers: {},
        trophies: {},
        timestamp: null,
        apiCache: {} 
      };

      const savedConfig = localStorage.getItem(CONFIG_KEY);
      if (!savedConfig) {
        return defaultConfig;
      }

      try {
        const config = JSON.parse(savedConfig);
        // Проверяем, не устарел ли кэш
        if (config && config.timestamp && Date.now() - config.timestamp < CACHE_EXPIRY) {
          // Убедимся, что все необходимые поля присутствуют
          return {
            ...defaultConfig,
            ...config
          };
        }
      } catch (parseError) {
        console.error('Error parsing config JSON:', parseError);
        // Если произошла ошибка при парсинге - сбрасываем кэш
        localStorage.removeItem(CONFIG_KEY);
      }
      
      return defaultConfig;
    } catch (error) {
      console.error('Error loading config:', error);
      return { 
        leagues: null, 
        teams: {}, 
        fixtures: {}, 
        players: {}, 
        statistics: {},
        standings: {},
        predictions: {},
        countries: null,
        seasons: null,
        headToHead: {},
        transfers: {},
        trophies: {},
        timestamp: null,
        apiCache: {} 
      };
    }
  }

  saveConfig(data) {
    if (!data) {
      console.warn('🚫 Попытка сохранить пустые данные в конфигурацию');
      return false;
    }
    
    try {
      // Получаем актуальные данные из this.config, или используем значения по умолчанию
      const currentConfig = this.config || {
        leagues: null, 
        teams: {}, 
        fixtures: {}, 
        players: {}, 
        statistics: {},
        standings: {},
        predictions: {},
        countries: null,
        seasons: null,
        headToHead: {},
        transfers: {},
        trophies: {},
        timestamp: null,
        apiCache: {}
      };
      
      // Создаем обновленную конфигурацию
      const config = {
        ...currentConfig,
        ...data,
        timestamp: Date.now()
      };
      
      // Сохраняем в localStorage
      try {
        const configString = JSON.stringify(config);
        console.log(`💾 Сохраняем в localStorage (размер: ${configString.length} байт)`);
        localStorage.setItem(CONFIG_KEY, configString);
        this.config = config;
        return true;
      } catch (storageError) {
        // Проверяем, не превышен ли лимит localStorage (обычно 5 МБ)
        if (storageError.name === 'QuotaExceededError' || 
            storageError.code === 22 || 
            storageError.code === 1014) {
          console.error('❌ Превышен лимит localStorage. Очищаем кэш API.');
          
          // Очищаем только часть кэша
          const reducedConfig = {
            ...config,
            apiCache: {}  // Сбрасываем apiCache, но оставляем другие данные
          };
          
          localStorage.setItem(CONFIG_KEY, JSON.stringify(reducedConfig));
          this.config = reducedConfig;
          return false;
        } else {
          throw storageError;  // Если другая ошибка, пробрасываем дальше
        }
      }
    } catch (error) {
      console.error('❌ Ошибка сохранения конфигурации:', error);
      return false;
    }
  }

  // Общие методы для работы с данными API
  getCachedData(endpoint, params = {}) {
    console.log(`🔍 Проверяем кэш для ${endpoint}`, params);
    console.log('🔧 Текущая конфигурация:', this.config);
    
    const key = this._generateCacheKey(endpoint, params);
    console.log(`🔑 Ключ кэша: ${key}`);
    
    // Проверяем, что apiCache существует
    if (!this.config || !this.config.apiCache) {
      console.warn('⚠️ Кэш API не инициализирован');
      return null;
    }
    
    const cacheItem = this.config.apiCache[key];
    console.log(`📦 Найденный элемент кэша:`, cacheItem);
    
    if (cacheItem && this.isCacheValid(cacheItem)) {
      console.log(`✅ Данные в кэше актуальны для ${endpoint}`);
      return cacheItem.data;
    }
    
    if (cacheItem) {
      console.log(`⏰ Данные в кэше устарели для ${endpoint}`);
    } else {
      console.log(`❌ Данные не найдены в кэше для ${endpoint}`);
    }
    
    return null;
  }

  saveApiData(endpoint, params = {}, data) {
    console.log(`💾 Сохраняем данные в кэш для ${endpoint}`, params);
    
    if (!data) {
      console.warn('⚠️ Попытка сохранить пустые данные в кэш');
      return false;
    }
    
    const key = this._generateCacheKey(endpoint, params);
    console.log(`🔑 Ключ кэша: ${key}`);
    
    // Убедимся, что apiCache существует
    const currentApiCache = this.config.apiCache || {};
    
    const updatedCache = {
      ...currentApiCache,
      [key]: {
        data,
        timestamp: Date.now()
      }
    };
    
    const result = this.saveConfig({ apiCache: updatedCache });
    console.log(`💾 Результат сохранения в кэш: ${result ? 'успешно' : 'ошибка'}`);
    
    return result;
  }

  _generateCacheKey(endpoint, params) {
    // Убедимся, что нам передали валидные параметры
    const safeEndpoint = endpoint || '';
    const safeParams = params || {};
    return `${safeEndpoint}_${JSON.stringify(safeParams)}`;
  }

  // Специализированные методы для конкретных данных
  getLeagues() {
    return this.config?.leagues || null;
  }

  getTeams(leagueId) {
    if (!this.config || !this.config.teams) return null;
    return this.config.teams[leagueId] || null;
  }

  getFixtures(params = {}) {
    if (!this.config || !this.config.fixtures) return null;
    const key = JSON.stringify(params || {});
    return this.config.fixtures[key] || null;
  }

  getPlayers(teamId) {
    if (!this.config || !this.config.players) return null;
    return this.config.players[teamId] || null;
  }

  getStatistics(fixtureId, teamId) {
    if (!this.config || !this.config.statistics) return null;
    const key = `${fixtureId}_${teamId}`;
    return this.config.statistics[key] || null;
  }

  getStandings(leagueId, season) {
    if (!this.config || !this.config.standings) return null;
    const key = `${leagueId}_${season}`;
    return this.config.standings[key] || null;
  }

  getPredictions(fixtureId) {
    if (!this.config || !this.config.predictions) return null;
    return this.config.predictions[fixtureId] || null;
  }

  saveLeagues(leagues) {
    if (!leagues) return false;
    return this.saveConfig({ leagues });
  }

  saveTeams(leagueId, teams) {
    if (!leagueId || !teams) return false;
    const currentTeams = this.config.teams || {};
    const updatedTeams = {
      ...currentTeams,
      [leagueId]: teams
    };
    return this.saveConfig({ teams: updatedTeams });
  }

  saveFixtures(params, fixtures) {
    if (!params || !fixtures) return false;
    const key = JSON.stringify(params);
    const currentFixtures = this.config.fixtures || {};
    const updatedFixtures = {
      ...currentFixtures,
      [key]: fixtures
    };
    return this.saveConfig({ fixtures: updatedFixtures });
  }

  savePlayers(teamId, players) {
    if (!teamId || !players) return false;
    const currentPlayers = this.config.players || {};
    const updatedPlayers = {
      ...currentPlayers,
      [teamId]: players
    };
    return this.saveConfig({ players: updatedPlayers });
  }

  saveStatistics(fixtureId, teamId, statistics) {
    if (!fixtureId || !teamId || !statistics) return false;
    const key = `${fixtureId}_${teamId}`;
    const currentStatistics = this.config.statistics || {};
    const updatedStatistics = {
      ...currentStatistics,
      [key]: statistics
    };
    return this.saveConfig({ statistics: updatedStatistics });
  }

  saveStandings(leagueId, season, standings) {
    if (!leagueId || !season || !standings) return false;
    const key = `${leagueId}_${season}`;
    const currentStandings = this.config.standings || {};
    const updatedStandings = {
      ...currentStandings,
      [key]: standings
    };
    return this.saveConfig({ standings: updatedStandings });
  }

  savePredictions(fixtureId, predictions) {
    if (!fixtureId || !predictions) return false;
    const currentPredictions = this.config.predictions || {};
    const updatedPredictions = {
      ...currentPredictions,
      [fixtureId]: predictions
    };
    return this.saveConfig({ predictions: updatedPredictions });
  }

  clearConfig() {
    try {
      localStorage.removeItem(CONFIG_KEY);
      this.config = { 
        leagues: null, 
        teams: {}, 
        fixtures: {}, 
        players: {}, 
        statistics: {},
        standings: {},
        predictions: {},
        countries: null,
        seasons: null,
        headToHead: {},
        transfers: {},
        trophies: {},
        timestamp: null,
        apiCache: {} 
      };
      return true;
    } catch (error) {
      console.error('Error clearing config:', error);
      return false;
    }
  }

  clearCacheItem(type, id = null) {
    try {
      if (!this.config || !type) return false;
      
      if (id && this.config[type] && this.config[type][id]) {
        const updatedType = { ...this.config[type] };
        delete updatedType[id];
        this.saveConfig({ [type]: updatedType });
        return true;
      } 
      
      if (!id && this.config[type]) {
        this.saveConfig({ [type]: type === 'leagues' ? null : {} });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error(`Error clearing cache for ${type}:`, error);
      return false;
    }
  }

  isConfigValid() {
    if (!this.config) return false;
    return this.config.timestamp && Date.now() - this.config.timestamp < CACHE_EXPIRY;
  }

  isCacheValid(cacheItem) {
    if (!cacheItem) return false;
    return cacheItem.timestamp && Date.now() - cacheItem.timestamp < CACHE_EXPIRY;
  }

  // Методы для доступа к кэшированным данным
  getCountries() {
    return this.config?.countries || null;
  }

  getSeasons() {
    return this.config?.seasons || null;
  }

  getHeadToHead(h2h) {
    if (!this.config || !this.config.headToHead) return null;
    return this.config.headToHead[h2h] || null;
  }

  getTransfers(playerId) {
    if (!this.config || !this.config.transfers) return null;
    return this.config.transfers[playerId] || null;
  }

  getTrophies(playerId) {
    if (!this.config || !this.config.trophies) return null;
    return this.config.trophies[playerId] || null;
  }

  // Методы для сохранения данных в кэш
  saveCountries(countries) {
    if (!countries) return false;
    return this.saveConfig({ countries });
  }

  saveSeasons(seasons) {
    if (!seasons) return false;
    return this.saveConfig({ seasons });
  }

  savePlayers(teamId, season, players) {
    if (!teamId || !season || !players) return false;
    const key = `${teamId}_${season}`;
    const currentPlayers = this.config.players || {};
    const updatedPlayers = {
      ...currentPlayers,
      [key]: players
    };
    return this.saveConfig({ players: updatedPlayers });
  }

  saveStatistics(leagueId, season, teamId, statistics) {
    if (!leagueId || !season || !teamId || !statistics) return false;
    const key = `${leagueId}_${season}_${teamId}`;
    const currentStats = this.config.statistics || {};
    const updatedStats = {
      ...currentStats,
      [key]: statistics
    };
    return this.saveConfig({ statistics: updatedStats });
  }

  saveHeadToHead(h2h, data) {
    if (!h2h || !data) return false;
    
    // Добавляем метку времени к данным
    const dataWithTimestamp = {
      ...data,
      timestamp: data.timestamp || new Date().toISOString()
    };
    
    const currentH2H = this.config.headToHead || {};
    const updatedH2H = {
      ...currentH2H,
      [h2h]: dataWithTimestamp
    };
    return this.saveConfig({ headToHead: updatedH2H });
  }

  saveTransfers(playerId, transfers) {
    if (!playerId || !transfers) return false;
    const currentTransfers = this.config.transfers || {};
    const updatedTransfers = {
      ...currentTransfers,
      [playerId]: transfers
    };
    return this.saveConfig({ transfers: updatedTransfers });
  }

  saveTrophies(playerId, trophies) {
    if (!playerId || !trophies) return false;
    const currentTrophies = this.config.trophies || {};
    const updatedTrophies = {
      ...currentTrophies,
      [playerId]: trophies
    };
    return this.saveConfig({ trophies: updatedTrophies });
  }

  // Метод для получения статистики кэша
  getCacheStats() {
    if (!this.config) return null;
    
    const now = Date.now();
    const expiryTime = now - CACHE_EXPIRY;
    
    // Подсчет количества элементов в каждом типе кэша
    const stats = {
      timestamp: this.config.timestamp,
      isValid: this.config.timestamp && this.config.timestamp > expiryTime,
      expiresAt: this.config.timestamp ? new Date(this.config.timestamp + CACHE_EXPIRY).toLocaleString() : null,
      leagues: {
        hasData: !!this.config.leagues && Array.isArray(this.config.leagues),
        count: this.config.leagues && Array.isArray(this.config.leagues) ? this.config.leagues.length : 0
      },
      teams: {
        hasData: !!this.config.teams && Object.keys(this.config.teams).length > 0,
        count: this.config.teams ? Object.keys(this.config.teams).length : 0,
        leagues: this.config.teams ? Object.keys(this.config.teams).join(', ') : ''
      },
      fixtures: {
        hasData: !!this.config.fixtures && Object.keys(this.config.fixtures).length > 0,
        count: this.config.fixtures ? Object.keys(this.config.fixtures).length : 0
      },
      players: {
        hasData: !!this.config.players && Object.keys(this.config.players).length > 0,
        count: this.config.players ? Object.keys(this.config.players).length : 0
      },
      statistics: {
        hasData: !!this.config.statistics && Object.keys(this.config.statistics).length > 0,
        count: this.config.statistics ? Object.keys(this.config.statistics).length : 0
      },
      standings: {
        hasData: !!this.config.standings && Object.keys(this.config.standings).length > 0,
        count: this.config.standings ? Object.keys(this.config.standings).length : 0
      },
      countries: {
        hasData: !!this.config.countries && Array.isArray(this.config.countries),
        count: this.config.countries && Array.isArray(this.config.countries) ? this.config.countries.length : 0
      },
      seasons: {
        hasData: !!this.config.seasons && Array.isArray(this.config.seasons),
        count: this.config.seasons && Array.isArray(this.config.seasons) ? this.config.seasons.length : 0
      },
      headToHead: {
        hasData: !!this.config.headToHead && Object.keys(this.config.headToHead).length > 0,
        count: this.config.headToHead ? Object.keys(this.config.headToHead).length : 0
      },
      transfers: {
        hasData: !!this.config.transfers && Object.keys(this.config.transfers).length > 0,
        count: this.config.transfers ? Object.keys(this.config.transfers).length : 0
      },
      trophies: {
        hasData: !!this.config.trophies && Object.keys(this.config.trophies).length > 0,
        count: this.config.trophies ? Object.keys(this.config.trophies).length : 0
      },
      apiCache: {
        hasData: !!this.config.apiCache && Object.keys(this.config.apiCache).length > 0,
        count: this.config.apiCache ? Object.keys(this.config.apiCache).length : 0
      },
      totalItems: 0,
      cacheSize: this._getCacheSize()
    };
    
    // Подсчет общего количества элементов
    stats.totalItems = 
      stats.leagues.count + 
      stats.teams.count + 
      stats.fixtures.count +
      stats.players.count +
      stats.statistics.count +
      stats.standings.count +
      stats.countries.count +
      stats.seasons.count +
      stats.headToHead.count +
      stats.transfers.count +
      stats.trophies.count +
      stats.apiCache.count;
    
    return stats;
  }
  
  // Вспомогательный метод для оценки размера кэша
  _getCacheSize() {
    try {
      const configString = JSON.stringify(this.config);
      const bytes = new Blob([configString]).size;
      
      // Форматирование размера
      if (bytes < 1024) {
        return `${bytes} байт`;
      } else if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(2)} КБ`;
      } else {
        return `${(bytes / (1024 * 1024)).toFixed(2)} МБ`;
      }
    } catch (error) {
      console.error('Ошибка при определении размера кэша:', error);
      return 'Неизвестно';
    }
  }
}

export default new ConfigService(); 